import React, { useState, useEffect, useContext } from "react";
import {
  Card,
  Badge,
  Button,
  Spinner,
  Form,
  Row,
  Col,
  Dropdown,
  ButtonGroup,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import { formatDistance } from "date-fns";
import pt from "date-fns/locale/pt-BR";
import {
  IconUsers,
  IconPlus,
  IconEye,
  IconEdit,
  IconTrash,
  IconShield,
  IconPhone,
} from "@tabler/icons-react";
import Swal from "sweetalert2";
import { createPortal } from "react-dom";
import styled from "styled-components";
import NotificationsContext from "../../contexts/NotificationsContext";
import { deleteUser, listUsers } from "./services/UserServicesV2";
import { getUserStatusBadge, SUPERUSER_LEVELS } from "./utils";
import { formatPhone } from "../Customers/utils";
import DatatablePaginated from "../../jsx/components/DatatablePaginated";
import UserModal from "./modal/UserModal";
import UserPermissionsModal from "./modal/UserPermissionsModal";

const UserList = () => {
  const { setNotify } = useContext(NotificationsContext);

  // Estados principais
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalRows, setTotalRows] = useState(0);
  const [selectedRows, setSelectedRows] = useState([]);

  // Estados dos modais
  const [showUserModal, setShowUserModal] = useState(false);
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);

  // Estados de filtros
  const [filters, setFilters] = useState({
    search: "",
    is_superuser: "",
    superuser_level: "",
    is_active: "",
    page: 1,
    page_size: 15,
    ordering: "-id",
  });

  useEffect(() => {
    fetchUsers();
  }, [filters]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await listUsers(filters);
      setUsers(response.data.results || response.data || []);
      setTotalRows(response.data.count || response.data.length || 0);
    } catch (error) {
      console.error("Erro ao buscar usuários:", error);
      setNotify({
        title: "Erro!",
        message: "Erro ao carregar lista de usuários",
        type: "error",
        visible: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (name, value) => {
    setFilters((prev) => ({
      ...prev,
      [name]: value,
      page: 1, // Reset página quando filtros mudam
    }));
  };

  const handlePageChange = (page) => {
    setFilters((prev) => ({
      ...prev,
      page: page,
    }));
  };

  const handleCreateUser = () => {
    setSelectedUser(null);
    setIsEditMode(false);
    setShowUserModal(true);
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setIsEditMode(true);
    setShowUserModal(true);
  };

  const handleViewPermissions = (user) => {
    setSelectedUser(user);
    setShowPermissionsModal(true);
  };

  const handleDeleteUser = (user) => {
    Swal.fire({
      title: "Tem certeza?",
      text: `Deseja inativar o usuário "${user.first_name} ${user.last_name}"? Esta ação pode ser revertida.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Sim, inativar!",
      cancelButtonText: "Cancelar",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await deleteUser(user.id);
          setNotify({
            title: "Sucesso!",
            message: "Usuário inativado com sucesso!",
            type: "success",
            visible: true,
          });
          fetchUsers();
        } catch (error) {
          setNotify({
            title: "Erro!",
            message: "Erro ao inativar usuário",
            type: "error",
            visible: true,
          });
        }
      }
    });
  };

  const onSelectedRowsChange = (state) => {
    setSelectedRows(state.selectedRows || []);
  };

  const renderUserBadge = (user) => {
    const badge = getUserStatusBadge(user);
    return <Badge bg={badge.variant}>{badge.text}</Badge>;
  };

  const DropdownPortal = ({ children, show }) => {
    if (!show) return null;

    return createPortal(
      <div style={{ position: "absolute", zIndex: 9999 }}>{children}</div>,
      document.body
    );
  };

  const columns = [
    {
      name: "Usuário",
      selector: (row) => row.first_name,
      sortable: true,
      wrap: true,
      minWidth: "200px",
      cell: (row) => (
        <div className="d-flex align-items-center">
          {row.photo && (
            <img
              src={row.photo}
              alt={row.first_name}
              className="rounded-circle me-2"
              width="32"
              height="32"
              style={{ objectFit: "cover" }}
            />
          )}
          <div>
            <div className="fw-bold">
              {row.first_name} {row.last_name}
            </div>
            <small className="text-muted">{row.email}</small>
          </div>
        </div>
      ),
    },
    {
      name: "Contato",
      selector: (row) => row.phone,
      sortable: true,
      minWidth: "140px",
      cell: (row) => (
        <div>
          <div className="d-flex align-items-center">
            <IconPhone size={14} className="me-1 text-muted" />
            {formatPhone(row.phone)}
          </div>
          {row.mobile && row.mobile !== row.phone && (
            <div className="d-flex align-items-center mt-1">
              <IconPhone size={14} className="me-1 text-muted" />
              <small>{formatPhone(row.mobile)}</small>
            </div>
          )}
        </div>
      ),
    },
    {
      name: "Nível",
      selector: (row) => row.superuser_level,
      sortable: true,
      minWidth: "120px",
      cell: (row) => renderUserBadge(row),
    },
    {
      name: "CPF",
      selector: (row) => row.cpf,
      sortable: true,
      minWidth: "120px",
      cell: (row) => (
        <div>
          {row.cpf ? (
            <small className="font-monospace">
              {row.cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4")}
            </small>
          ) : (
            <small className="text-muted">Não informado</small>
          )}
        </div>
      ),
    },
    {
      name: "Status",
      selector: (row) => row.is_active,
      sortable: true,
      minWidth: "100px",
      cell: (row) => (
        <Badge bg={row.is_active ? "success" : "secondary"}>
          {row.is_active ? "Ativo" : "Inativo"}
        </Badge>
      ),
    },
    {
      name: "Último Login",
      selector: (row) => row.last_login?.created_at,
      sortable: true,
      minWidth: "140px",
      cell: (row) => (
        <div>
          {row.last_login?.created_at ? (
            <small>
              {formatDistance(new Date(row.last_login.created_at), new Date(), {
                addSuffix: true,
                locale: pt,
              })}
            </small>
          ) : (
            <small className="text-muted">Nunca</small>
          )}
        </div>
      ),
    },
    {
      name: "Ações",
      cell: (row) => (
        <Dropdown>
          <Dropdown.Toggle
            variant="outline-primary"
            size="sm"
            id={`dropdown-split-${row.id}`}
          />

          <DropdownPortal show={true}>
            <Dropdown.Menu style={{ zIndex: 9999 }}>
              <Dropdown.Item onClick={() => handleEditUser(row)}>
                <IconEdit size={16} className="me-2" />
                Editar usuário
              </Dropdown.Item>

              <Dropdown.Item onClick={() => handleViewPermissions(row)}>
                <IconShield size={16} className="me-2" />
                Gerenciar permissões
              </Dropdown.Item>

              <Dropdown.Divider />

              <Dropdown.Item
                className="text-danger"
                onClick={() => handleDeleteUser(row)}
              >
                <IconTrash size={16} className="me-2" />
                Inativar usuário
              </Dropdown.Item>
            </Dropdown.Menu>
          </DropdownPortal>
        </Dropdown>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: "100px",
    },
  ];

  return (
    <div className="main-container">
      <Card>
        <CardHeaderWrapper className="border-bottom">
          <Row className="d-flex w-100">
            <Col xs={12} md={6}>
              <h4>
                <IconUsers className="me-2" />
                Usuários
                {totalRows > 0 && (
                  <Badge bg="primary" className="ms-2">
                    {totalRows}
                  </Badge>
                )}
              </h4>
            </Col>
            <Col xs={12} md={6}>
              <Button
                variant="primary"
                size="sm"
                onClick={handleCreateUser}
                className="d-inline-flex align-items-center"
              >
                <IconPlus size={18} className="me-1" />
                <span className="d-none d-sm-inline">Novo Usuário</span>
                <span className="d-sm-none">Novo</span>
              </Button>
            </Col>
          </Row>
        </CardHeaderWrapper>

        <Card.Body className="p-0 p-md-3">
          {/* Filtros */}
          <Row className="mb-3 px-3 px-md-0">
            <Col xs={12} md={4} className="mb-2">
              <Form.Control
                type="text"
                placeholder="Buscar por nome, email ou telefone..."
                value={filters.search}
                onChange={(e) => handleFilterChange("search", e.target.value)}
                size="sm"
              />
            </Col>

            <Col xs={6} md={2} className="mb-2">
              <Form.Select
                value={filters.is_active}
                onChange={(e) =>
                  handleFilterChange("is_active", e.target.value)
                }
                size="sm"
              >
                <option value="">Todos Status</option>
                <option value="1">Ativo</option>
                <option value="0">Inativo</option>
              </Form.Select>
            </Col>

            <Col xs={6} md={2} className="mb-2">
              <Form.Select
                value={filters.is_superuser}
                onChange={(e) =>
                  handleFilterChange("is_superuser", e.target.value)
                }
                size="sm"
              >
                <option value="">Todos Tipos</option>
                <option value="1">Superusuários</option>
                <option value="0">Usuários Comuns</option>
              </Form.Select>
            </Col>

            <Col xs={12} md={4} className="mb-2">
              <Form.Select
                value={filters.superuser_level}
                onChange={(e) =>
                  handleFilterChange("superuser_level", e.target.value)
                }
                size="sm"
              >
                <option value="">Todos Níveis</option>
                {Object.entries(SUPERUSER_LEVELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Form.Select>
            </Col>
          </Row>

          {/* Tabela */}
          {loading ? (
            <div className="text-center p-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3">Carregando usuários...</p>
            </div>
          ) : users.length === 0 ? (
            <div className="text-center p-5">
              <IconUsers size={48} className="text-muted mb-3" />
              <h5 className="text-muted">Nenhum usuário encontrado</h5>
              <p className="text-muted">
                {filters.search || filters.is_active || filters.superuser_level
                  ? "Nenhum usuário corresponde aos filtros selecionados."
                  : "Comece criando seu primeiro usuário!"}
              </p>
              {!filters.search &&
                !filters.is_active &&
                !filters.superuser_level && (
                  <Button variant="primary" onClick={handleCreateUser}>
                    <IconPlus size={18} className="me-1" />
                    Criar Primeiro Usuário
                  </Button>
                )}
            </div>
          ) : (
            <DatatablePaginated
              componentName="user-list"
              data={users}
              columns={columns}
              totalRows={totalRows}
              handlePageChange={handlePageChange}
              pagination={true}
              paginationServer={true}
              paginationTotalRows={totalRows}
              paginationPerPage={filters.page_size}
              paginationDefaultPage={filters.page}
              isLoading={loading}
              selectable={false}
              filterByField={false}
              onSelectedRowsChange={onSelectedRowsChange}
              classes="px-0"
            />
          )}
        </Card.Body>
      </Card>

      {/* Modal de Cadastro/Edição */}
      <UserModal
        show={showUserModal}
        onHide={() => setShowUserModal(false)}
        user={selectedUser}
        isEdit={isEditMode}
        onSave={fetchUsers}
      />

      {/* Modal de Permissões */}
      <UserPermissionsModal
        show={showPermissionsModal}
        onHide={() => setShowPermissionsModal(false)}
        user={selectedUser}
        onSave={fetchUsers}
      />
    </div>
  );
};

const CardHeaderWrapper = styled(Card.Header)`
  .row div:nth-child(2) {
    display: flex;
    justify-content: end;
  }
  @media (max-width: 768px) {
    .row {
      text-align: center;
      gap: 20px;
    }
    .row div:nth-child(2) {
      display: flex;
      justify-content: center;
    }
  }
`;

export default UserList;
